/// <amd-module name="Build/Run/RunExternalService" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { BuildStatus } from "Build/Client/Contracts/Build";
import { IBuildLinkingService } from "Build/Client/Linking";
import { NavTestTabId } from "Build/Run/Constants";
import { getTFSPageData } from "Tfs/Platform/Page";
import { getTestResultsClient } from "Test/Client/RestClient/TestResults";
import { ITestManagementService } from "Test/Client/TestManagementServices";
import { PipelineTestMetrics, Metrics, CoverageDetailedSummaryStatus } from "Test/common-util/Contracts/Test";
import { TestSummaryReportUtils } from "Test/common-util/TestSummaryReport.Utils"
import * as Utils_String from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTraceService } from "VSS/Platform/Trace";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export class TestResults {
    public testsPassRate: number;
    public testsCount: number;

    constructor(testsCount: number, testsPassRate: number) {
        this.testsCount = testsCount;
        this.testsPassRate = testsPassRate;
    }
}

export interface IJobData {
    jobName: string;
    phaseName: string;
    stageName: string;
}

/**
 * Represents the code coverage summary results Used to publish or retrieve code coverage summary against a build
 */
export interface CodeCoverageSummary {
    coverage: string;
    coverageDetailedSummaryStatus: string;
}

export interface IRunExternalService extends IVssService {
    getTestResultsForRun(id: number, status: BuildStatus): Promise<TestResults | undefined>;
    getTestsResultsForJob(id: number, data: IJobData): Promise<TestResults | undefined>;
    getTestsResultsForStage(id: number, stageName: string): Promise<TestResults | undefined>;
    getTestTabLink(id: number): string;
    getCodeCoverageSummary(buildId: number, flag: number): Promise<CodeCoverageSummary | undefined>;
}

export class RunExternalService extends VssService implements IRunExternalService {
    private _projectId: string;
    private _projectName: string;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        const pageData = getTFSPageData(pageContext);
        this._projectId = pageData && pageData.project && pageData.project.id || "";
        this._projectName = pageData && pageData.project ? pageData.project.name : "";
    }

    public getTestTabLink(id: number) {
        const linkingService = this.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        return linkingService.getBuildUrl(id, NavTestTabId);
    }

    public async getTestResultsForRun(id: number, status: BuildStatus): Promise<TestResults | undefined> {
        await this._loadTcm();
        const tcmClient = getTestResultsClient(this.pageContext);
        const data = await tcmClient.getTestPipelineMetrics(this._projectId, id, undefined, undefined, undefined, [Metrics.ResultSummary]);
        if (data) {
            const results = this._getTestResults(data);
            // if the build has not completed then there might be some tests to run
            if (status !== BuildStatus.Completed) {
                results.testsCount = -1;
            }
            return results;
        }
    }

    public async getTestsResultsForJob(id: number, data: IJobData): Promise<TestResults | undefined> {
        await this._loadTcm();
        const tcmClient = getTestResultsClient(this.pageContext);
        const testData = await tcmClient.getTestPipelineMetrics(this._projectId, id, data.stageName, data.phaseName, data.jobName, [Metrics.ResultSummary]);
        if (testData) {
            return this._getTestResults(testData);
        }
    }

    public async getTestsResultsForStage(id: number, stageName: string): Promise<TestResults | undefined> {
        await this._loadTcm();
        const tcmClient = getTestResultsClient(this.pageContext);
        const testData = await tcmClient.getTestPipelineMetrics(this._projectId, id, stageName, undefined, undefined, [Metrics.ResultSummary]);
        if (testData) {
            return this._getTestResults(testData);
        }
    }

    private _getTestResults(data: PipelineTestMetrics) {
        let testsPassRate = -1;
        let testsCount = -1;
        if (data && data.resultSummary && data.resultSummary) {
            let passTestStatistics = TestSummaryReportUtils.getPassedTestStatisticsInPipeline(data.resultSummary);
            if(passTestStatistics && passTestStatistics.totalTestCount > 0) {
                testsPassRate = passTestStatistics.passTestRate;
                testsCount = passTestStatistics.totalTestCount;
                if (Number.isNaN(testsPassRate)) {
                    testsPassRate = 0;
                }
            }
            else if(passTestStatistics && passTestStatistics.totalTestCount === 0 && passTestStatistics.totalNotReportedTestCount > 0){
                //indicates all results are in not reported state.
                testsPassRate = -2;
            }
        }

        return new TestResults(testsCount, testsPassRate);
    }

      /* -10 is a special value for deltaBuildId. 
       * With this usage caller making get code coverage summary call with deltabuildid = -10 does not expect 
       * delta computation for coverage and therefore that additional computation can be avoided.
      */
    public async getCodeCoverageSummary(buildId: number): Promise<CodeCoverageSummary | undefined> {
        await this._loadTcm();
        const tcmClient = getTestResultsClient(this.pageContext);
        const defaultDeltaBuildId = -10;
        const coverageSummary = await tcmClient.getCodeCoverageSummary(this._projectName, buildId, defaultDeltaBuildId);
        let defaultCodeCoverageTab = isFeatureFlagEnabled(this.pageContext, "WebAccess.TestManagement.CodeCoverage.EnableTabByDefault", false);
        let coveragedetailed = "";
        if (defaultCodeCoverageTab && coverageSummary.coverageDetailedSummaryStatus) {
            coveragedetailed = coverageSummary.coverageDetailedSummaryStatus.toString();
        }

        let coveredPercentage = new Array<number>();
        if (coverageSummary && coverageSummary.coverageData && coverageSummary.coverageData.length > 0) {
            const coverageData = coverageSummary.coverageData;
            coverageData.forEach(data => {
                const lineStats = data.coverageStats.filter(
                    value =>
                        value.label &&
                        (!Utils_String.localeIgnoreCaseComparer(value.label, "Lines") || !Utils_String.localeIgnoreCaseComparer(value.label, "Line"))
                );
                
                let linesCovered = 0;
                let totalLines;
                if (lineStats.length) {
                    linesCovered = lineStats[0].covered || 0;
                    totalLines = lineStats[0].total || lineStats[0].covered;
                }
                
                coveredPercentage.push(totalLines ? (linesCovered / totalLines) * 100 : 0);
            });
            const averageCoverage = (coveredPercentage.reduce<number>((total, newCoverage) => total + newCoverage, 0) / coveredPercentage.length).toFixed(2);

            return { coverage: averageCoverage, coverageDetailedSummaryStatus: coveragedetailed };
        }

        if (defaultCodeCoverageTab)
            return { coverage: "0", coverageDetailedSummaryStatus: coveragedetailed };

        return undefined;
    }


    private async _loadTcm() {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        await contributionService.getContributionAsync("ms.vss-build-web.tcm-service-location");
    }
}

Services.add("IRunExternalService", { serviceFactory: RunExternalService });
